import { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Page from '../pages/Page'
import useQueryLibrary from '../../hooks/useQueryLibrary'
import FetchContext from '../../context/fetch/FetchContext'
import { useNotificationContext } from '../../units/notifications/NotificationContext'
import { resolveErrorMessage } from '../../helpers/error.helper'
import { rootDataResponseHandler } from '../../helpers/responseHandlers'
import useAnnouncementPreview from './useAnnouncementPreview'
import AnnouncementForm from './AnnouncementForm'
import AnnouncementPopup from './AnnouncementPopup'
import PublishConfirmation from './PublishConfirmation'
import { resolveAnnouncementFormValues } from '../../data-organizers/announcements/resolveAnnouncementFormValues'
import { resolveAnnouncementData } from '../../data-organizers/announcements/resolveAnnouncementData'

export const NEW_ANNOUNCEMENT = 'add'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

// The upload endpoint answers with the files at the root rather than under `data`.
async function uploadImage(fetcher, file) {
    const body = new FormData()
    body.append('files', file)

    const [uploaded] = await fetcher.authenticatedFetch({
        method: 'post',
        path: `/upload`,
        data: body,
        responseHandler: rootDataResponseHandler,
    })

    return uploaded
}

export default function AnnouncementEditorPage() {
    const { documentId } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const queryLibrary = useQueryLibrary()
    const fetcher = useContext(FetchContext)
    const { addNotification } = useNotificationContext()
    const { preview, openPreview, closePreview } = useAnnouncementPreview()
    const [isConfirmingPublish, setConfirmingPublish] = useState(false)

    const isNew = documentId === NEW_ANNOUNCEMENT

    const {
        data: announcement,
        isFetching,
        isLoading,
    } = useQuery({
        queryKey: queryLibrary.oneAnnouncementQuery(documentId),
        queryFn: () => fetcher.fetchAnnouncement(documentId),
        enabled: !isNew,
    })

    const formValues = resolveAnnouncementFormValues(isNew ? null : announcement)

    const refresh = async () => {
        await queryClient.invalidateQueries({
            queryKey: queryLibrary.oneAnnouncementQuery(documentId),
        })
        await queryClient.invalidateQueries({
            queryKey: queryLibrary.announcementsQuery(),
        })
    }

    const report = (error) => {
        addNotification({
            type: 'error',
            content: resolveErrorMessage(error),
        })
    }

    const saveMutation = useMutation({
        mutationFn: async (values) => {
            const data = resolveAnnouncementData(values)

            const saved = await fetcher.authenticatedFetch({
                method: isNew ? 'post' : 'put',
                path: isNew ? `/announcements` : `/announcements/${documentId}`,
                data: { data },
                headers: JSON_HEADERS,
            })

            if (values.imageFile == null) {
                return saved
            }

            const uploaded = await uploadImage(fetcher, values.imageFile)

            return fetcher.authenticatedFetch({
                method: 'put',
                path: `/announcements/${saved.documentId}/image`,
                data: { data: { image: uploaded.id } },
                headers: JSON_HEADERS,
            })
        },
        onSuccess: async (saved) => {
            if (isNew) {
                navigate(`/announcements/${saved.documentId}`)
            }
            else {
                await refresh()
            }

            addNotification({
                type: 'success',
                content: 'המודעה נשמרה בהצלחה',
            })
        },
        onError: report,
    })

    const handleSubmit = (values) => {
        saveMutation.mutate(values)
    }

    const publishMutation = useMutation({
        mutationFn: () => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${documentId}/publish`,
        }),
        onSuccess: refresh,
        onError: report,
    })

    const handlePublish = () => {
        setConfirmingPublish(true)
    }

    const confirmPublish = () => {
        setConfirmingPublish(false)
        publishMutation.mutate()
    }

    const unpublishMutation = useMutation({
        mutationFn: () => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${documentId}/unpublish`,
        }),
        onSuccess: refresh,
        onError: report,
    })

    const handleUnpublish = () => {
        unpublishMutation.mutate()
    }

    const cloneMutation = useMutation({
        mutationFn: () => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${documentId}/clone`,
        }),
        onSuccess: async (clone) => {
            await queryClient.invalidateQueries({ queryKey: queryLibrary.announcementsQuery() })
            navigate(`/announcements/${clone.documentId}`)
        },
        onError: report,
    })

    const handleClone = () => {
        cloneMutation.mutate()
    }

    const handlePreview = () => {
        openPreview(documentId)
    }

    const busy = isFetching
        || isLoading
        || saveMutation.isPending
        || publishMutation.isPending
        || unpublishMutation.isPending
        || cloneMutation.isPending

    const actionsDisabled = isNew || busy

    const storedStatus = formValues.isPublished ? 'published' : 'draft'

    const status = isNew
        ? 'new'
        : (announcement == null ? null : storedStatus)

    const commands = [
        {
            label: 'שמירה',
            type: 'submit',
            form: 'form',
            position: 'start',
            color: 'primary',
            disabled: busy,
        },
        {
            label: formValues.isPublished ? 'ביטול פרסום' : 'פרסום',
            onClick: formValues.isPublished ? handleUnpublish : handlePublish,
            position: 'start',
            color: 'warning',
            disabled: actionsDisabled,
        },
        {
            label: 'שכפול',
            onClick: handleClone,
            position: 'start',
            color: 'secondary',
            disabled: actionsDisabled,
        },
        {
            label: 'תצוגה מקדימה',
            onClick: handlePreview,
            position: 'end',
            color: 'secondary',
            disabled: actionsDisabled,
        },
        {
            label: 'נתונים',
            onClick: () => navigate(`/announcements/${documentId}/stats`),
            position: 'end',
            color: 'secondary',
            disabled: actionsDisabled,
        },
    ]

    return (
        <Page
            commands={commands}
            className="announcement-editor"
            title={isNew ? 'מודעה חדשה' : announcement?.name ?? 'מודעה'}
            status={status}
            busy={busy}
        >
            <AnnouncementForm
                announcement={formValues}
                onSubmit={handleSubmit}
            />
            <PublishConfirmation
                open={isConfirmingPublish}
                isContentLocked={formValues.isContentLocked}
                onConfirm={confirmPublish}
                onCancel={() => setConfirmingPublish(false)}
            />
            <AnnouncementPopup
                workingSet={preview}
                onClose={closePreview}
            />
        </Page>
    )
}
